'use client';

import React, { createContext, PropsWithChildren, ReactNode, useContext, useState } from 'react';
import { NotificationItem, NotificationMode, NotificationType } from '@/src/app/components/Notification/types';
import NotificationList from '@/src/app/components/NotificationList/NotificationList';
import NotificationTransition from '@/src/app/components/FlashNotification/NotificationTransition';
import { Dictionary } from '@/src/lib/dictionary/types';
type ShowNotificationFactory = (message: string | ReactNode, type: NotificationType, notificationType?: NotificationMode, description?: string, autoClose?: boolean, autoCloseDuration?: number, isClosable?: boolean, id?: string) => NotificationItem | undefined;
type RemoveNotificationFactory = (id: string) => void;
export const NotificationContext = createContext({
  notifications: [] as Array<NotificationItem>,
  showNotification: (() => void 0) as ShowNotificationFactory,
  removeNotification: (() => void 0) as RemoveNotificationFactory
});
export const useNotification = () => {
  return useContext(NotificationContext);
};
export default function NotificationProvider({
  children,
  dict
}: PropsWithChildren<{
  dict: Dictionary;
}>) {
  const [notifications, setNotifications] = useState<Array<NotificationItem>>([]);
  const toasts = notifications.filter(toast => toast.mode === NotificationMode.TOAST);
  const flashNotifications = notifications.filter(toast_0 => toast_0.mode === NotificationMode.FLASH);
  const removeNotification: RemoveNotificationFactory = (id: string) => {
    setNotifications(prevNotifications => prevNotifications.filter(notification => notification.id !== id));
  };
  const showNotification: ShowNotificationFactory = (message: string | ReactNode, type: NotificationType, notificationType: NotificationMode = NotificationMode.FLASH, description?: string, autoClose: boolean = false, autoCloseDuration: number = 5, isClosable: boolean = true, id_0: string = `${Date.now() + Math.random()}`) => {
    if (notifications.find(notification_0 => notification_0.message === dict.messages.generic.notAuthorized)) return;
    const notification_1: NotificationItem = {
      id: id_0,
      message,
      description,
      type,
      isClosable,
      mode: notificationType
    };
    setNotifications(prevNotifications_0 => {
      const index = prevNotifications_0.findIndex(n => n.id === notification_1.id);
      if (index > -1) {
        return [...prevNotifications_0.toSpliced(index, 1), notification_1];
      }
      return [...prevNotifications_0, notification_1];
    });
    if (autoClose) {
      setTimeout(() => {
        removeNotification(notification_1.id);
      }, autoCloseDuration * 1000);
    }
    return notification_1;
  };
  return <NotificationContext.Provider value={{
    notifications,
    showNotification,
    removeNotification
  }}>
      {toasts.length ? <NotificationList data={toasts} className="toast-list right-0 z-[60] p-4 max-w-lg">
          {toasts.reverse().map(toast_1 => <NotificationTransition id={toast_1.id} key={toast_1.id} type={toast_1.type} message={toast_1.message} onClose={() => removeNotification(toast_1.id)} isToast />)}
        </NotificationList> : null}
      {flashNotifications.length ? <NotificationList className="flash-notification-list inset-x-0 z-50 space-y-4" data={flashNotifications}>
          {flashNotifications.reverse().map(notification_2 => <NotificationTransition id={notification_2.id} key={notification_2.id} type={notification_2.type} message={notification_2.message} description={notification_2.description} isClosable={notification_2.isClosable} onClose={() => removeNotification(notification_2.id)} />)}
        </NotificationList> : null}
      {children}
    </NotificationContext.Provider>;
}